import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleDollar, faArrowRight} from "@fortawesome/pro-regular-svg-icons";
import toast from "react-hot-toast";
import { useAuth, Roles } from "../../providers/AuthContext";
import { createCreditCdr, emailSessionReceipt, getSessionDetails, getSessionReceiptPdf } from "../../api/ChargingSession";
import { formatSwedishTime } from "../../helpers/DateTime";
import LoadingSpinner from "../LoadingSpinner";
import ConfirmationModal from "../../components/ConfirmationModal";
import { FormGrid } from "../FormCard";
import { DetailRow, InputRow } from "../Rows";
import { getStatusColorStyle } from "../../helpers/StatusMapper";
import { useTranslation } from "react-i18next";

const displayValue = (value) => {
    if (value == null) {
        return "-";
    }

    if (typeof value === "string" && value.trim() === "") {
        return "-";
    }

    return value;
};

const formatDecimal = (value, suffix = "", decimals = 2) => {
    if (value == null || Number.isNaN(Number(value))) {
        return "-";
    }

    return `${Number(value).toFixed(decimals)}${suffix}`;
};

const isReceiptEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? "").trim());

const cardPaymentWarningKey = (status) => {
    const normalized = String(status ?? "").trim().toLowerCase();
    if (normalized === "partiallypaid") {
        return "session.paymentPartiallyPaid";
    }

    if (normalized === "failed" || normalized === "uncollectible") {
        return "session.paymentNotCollected";
    }

    return null;
};

const isGitoCardSession = (details) => {
    const candidates = [
        details?.paymentMethodName,
        details?.startMethod,
        details?.authMethod,
        details?.receipt?.startMethod,
    ];

    return candidates.some((value) => String(value ?? "").trim().toUpperCase() === "GITO_CARD");
};

const formatPaymentMethod = (paymentMethodName, t) => {
    const normalized = String(paymentMethodName ?? "").trim().toLowerCase();

    if (normalized === "invoice") {
        return t("payment.Invoice");
    }

    if (normalized === "card") {
        return t("payment.Card");
    }

    if (normalized === "other" || normalized === "unknown") {
        return t("payment.Other");
    }

    return paymentMethodName;
};

const formatPaymentStatus = (status, t) => {
    const normalized = String(status ?? "").trim().toLowerCase();
    const keyByStatus = {
        paid: "session.receipt.status.paid",
        partiallypaid: "session.receipt.status.partiallyPaid",
        failed: "session.receipt.status.failed",
        uncollectible: "session.receipt.status.uncollectible",
        processing: "session.receipt.status.processing",
    };
    const key = keyByStatus[normalized];

    if (!key) {
        return status;
    }

    return t(key);
};

const formatPercentRange = (start, end) => {
    if (start == null && end == null) {
        return "-";
    }

    if (start != null && end != null) {
        return (
            <span className="inline-flex items-center gap-1">
                {Math.round(start)}%
                <FontAwesomeIcon icon={faArrowRight} />
                {Math.round(end)}%
            </span>
        );
    }

    const single = start ?? end;
    return `${Math.round(single)}%`;
};
function DetailItem({ label, value, children }) {
    const content = children ?? displayValue(value);

    return (
        <div className="min-w-0">
            <dt className="text-xs font-bold uppercase tracking-wide text-black">
                {label}
            </dt>

            <dd
                className="mt-0.5 break-words text-sm text-black"
                title={typeof content === "string" && content !== "-" ? content : undefined}
            >
                {content}
            </dd>
        </div>
    );
}

function SessionDetailsSummary({
    chargingSessionPublicId,
    isOpen,
    showSpinner = true,
    onLoadingChange,
}) {
    const { t } = useTranslation();
    const { getAccessToken, hasRole } = useAuth();
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [creditModalOpen, setCreditModalOpen] = useState(false);
    const [crediting, setCrediting] = useState(false);
    const [receiptEmail, setReceiptEmail] = useState("");
    const [receiptEmailInvalid, setReceiptEmailInvalid] = useState(false);
    const [openingReceipt, setOpeningReceipt] = useState(false);
    const [sendingReceipt, setSendingReceipt] = useState(false);

    const setLoadingState = useCallback((isLoading) => {
        setLoading(isLoading);
        if (typeof onLoadingChange === "function") {
            onLoadingChange(isLoading);
        }
    }, [onLoadingChange]);

    const loadDetails = useCallback(async () => {
        const token = await getAccessToken();
        return getSessionDetails(token, chargingSessionPublicId);
    }, [chargingSessionPublicId, getAccessToken]);

    useEffect(() => {
        if (!isOpen || !chargingSessionPublicId) {
            setLoadingState(false);
            return;
        }

        let cancelled = false;

        const fetchDetails = async () => {
            setLoadingState(true);
            setError(null);

            try {
                const response = await loadDetails();

                if (!cancelled) {
                    setDetails(response ?? null);
                }
            } catch (err) {
                if (!cancelled) {
                    setDetails(null);
                    setError(err?.message || t("session.failedLoadDetails"));
                }
            } finally {
                if (!cancelled) {
                    setLoadingState(false);
                }
            }
        };

        fetchDetails();

        return () => {
            cancelled = true;
        };
    }, [chargingSessionPublicId, isOpen, loadDetails, setLoadingState]);

    useEffect(() => {
        const suggested = details?.receipt?.suggestedEmail;
        setReceiptEmail(typeof suggested === "string" ? suggested : "");
        setReceiptEmailInvalid(false);
    }, [chargingSessionPublicId, details?.receipt?.suggestedEmail]);

    if (!isOpen || !chargingSessionPublicId) {
        return null;
    }

    if (loading && showSpinner) {
        return (
            <div className="mb-4 flex justify-center py-6">
                <LoadingSpinner />
            </div>
        );
    }

    if (loading) {
        return null;
    }

    if (error) {
        return (
            <div className="mb-4 rounded-md border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700">
                {error}
            </div>
        );
    }

    if (!details) {
        return null;
    }

    const tokenName = details.tokenName?.trim() ?? "";
    const tokenVisualNumber = details.tokenVisualNumber?.trim() ?? "";
    const tokenLabel = tokenName ? tokenName : tokenVisualNumber || "";


    const showCdrFields = details.showCdrInfo && hasRole(Roles.Admin);
    const totalCostFormatted = formatDecimal(details.totalCost);
    const totalCostDisplay =
        totalCostFormatted === "-"
            ? "-"
            : `${totalCostFormatted} ${details.currencyCode || "SEK"}`;

    const hasCdr = Boolean(details.cdrPublicId?.trim());
    const hasCreditCdr = Boolean(details.creditCdrPublicId?.trim());
    const canCreditCdr = hasCdr && !hasCreditCdr;

    const creditConfirmTitle = t("session.creditCdrConfirm");
    const creditConfirmDescription = details.isCdrInvoiced === true
        ? t("session.creditCdrInvoicedHelp")
        : t("session.creditCdrHelp");

    const handleCreditCdrConfirm = async () => {
        setCreditModalOpen(false);
        setCrediting(true);
        const toastId = toast.loading(t("toast.creating", { entity: t("entity.creditCdr") }));

        try {
            const token = await getAccessToken();
            const result = await createCreditCdr(token, details.cdrPublicId);
            const refreshed = await loadDetails();
            setDetails(refreshed ?? null);

            if (result?.warning) {
                toast(result.warning, { id: toastId, icon: "⚠️" });
            } else {
                toast.success(t("toast.created", { entity: t("entity.creditCdr") }), { id: toastId });
            }
        } catch (err) {
            toast.error(err?.message || t("toast.failedCreate", { entity: t("entity.creditCdr") }), { id: toastId });
        } finally {
            setCrediting(false);
        }
    };

    const receipt = details.receipt && typeof details.receipt === "object" ? details.receipt : null;
    const showReceipt = receipt != null && !isGitoCardSession(details);
    const cardPaymentWarning = showReceipt ? cardPaymentWarningKey(receipt?.paymentStatus) : null;
    const receiptCurrency = receipt?.currencyCode || details.currencyCode || "SEK";
    const sessionPublicId = details.chargingSessionPublicId || chargingSessionPublicId;

    const formatReceiptMoney = (value) => {
        const formatted = formatDecimal(value);
        if (formatted === "-") {
            return null;
        }

        return `${formatted} ${receiptCurrency}`;
    };

    const hasReceiptCost = (value) => {
        const amount = Number(value);
        return value != null && !Number.isNaN(amount) && amount !== 0;
    };

    const costBreakdown = details.costBreakdown && typeof details.costBreakdown === "object"
        ? details.costBreakdown
        : null;
    const receiptCosts = receipt
        ? {
            energyCost: receipt.energyCost,
            timeCost: receipt.timeCost,
            parkingCost: receipt.parkingCost,
            fixedCost: receipt.fixedCost,
            bookingFee: receipt.bookingFee,
            vatAmount: receipt.vatAmount,
        }
        : null;
    const costSource = costBreakdown ?? receiptCosts;
    const costRows = costSource
        ? [
            [t("session.costs.energy"), costSource.energyCost],
            [t("session.costs.time"), costSource.timeCost],
            [t("session.costs.parking"), costSource.parkingCost],
            [t("session.costs.fixed"), costSource.fixedCost],
            [t("session.costs.booking"), costSource.bookingFee],
            [t("session.costs.vat"), costSource.vatAmount],
        ]
            .filter(([, value]) => hasReceiptCost(value))
            .map(([label, value]) => ({ label, value: formatReceiptMoney(value) }))
        : [];

    const handleOpenReceipt = async () => {
        setOpeningReceipt(true);

        try {
            const token = await getAccessToken();
            const blob = await getSessionReceiptPdf(token, sessionPublicId);
            const pdfBlob = blob?.type === "application/pdf"
                ? blob
                : new Blob([blob], { type: "application/pdf" });
            const url = window.URL.createObjectURL(pdfBlob);
            const opened = window.open(url, "_blank", "noopener,noreferrer");

            if (!opened) {
                const link = document.createElement("a");
                link.href = url;
                link.target = "_blank";
                link.rel = "noopener noreferrer";
                link.click();
            }

            window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch (err) {
            toast.error(err?.message || t("session.receipt.failedOpen"));
        } finally {
            setOpeningReceipt(false);
        }
    };

    const handleSendReceipt = async () => {
        const email = receiptEmail.trim();

        if (!isReceiptEmail(email)) {
            setReceiptEmailInvalid(true);
            toast.error(t("session.receipt.invalidEmail"));
            return;
        }

        setSendingReceipt(true);
        const toastId = toast.loading(t("session.receipt.sending"));

        try {
            const token = await getAccessToken();
            await emailSessionReceipt(token, sessionPublicId, email);
            toast.success(t("session.receipt.sent"), { id: toastId });
        } catch (err) {
            toast.error(err?.message || t("session.receipt.failedSend"), { id: toastId });
        } finally {
            setSendingReceipt(false);
        }
    };

    const cardClassName = "rounded-lg border border-mute-200 bg-white p-4 shadow-lg";

    return (
        <div className="flex flex-col gap-4">
            <div className={cardClassName}>
                <h3 className="mb-3 text-sm font-bold text-black">{t("session.information")}</h3>

                <dl className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-5">
                    {hasRole(Roles.Admin) && (
                        <DetailItem
                            label={t("session.publicId")}
                            value={details.chargingSessionPublicId || chargingSessionPublicId}
                        />
                    )}
                    <DetailItem label={t("entity.site")} value={details.chargingSiteName} />
                    <DetailItem label={t("entity.charger")} value={details.chargingPointName} />
                    <DetailItem label={t("entity.connector")} value={details.chargingConnectorName} />
                    <DetailItem label={t("entity.customer")} value={details.customerName} />
                    <DetailItem label={t("entity.token")} value={tokenLabel} />
                    <DetailItem
                        label={t("form.startTime")}
                        value={
                            details.startDateTimeUtc
                                ? formatSwedishTime(details.startDateTimeUtc, false)
                                : null
                        }
                    />
                    <DetailItem
                        label={t("form.endTime")}
                        value={
                            details.endDateTimeUtc
                                ? formatSwedishTime(details.endDateTimeUtc, false)
                                : null
                        }
                    />
                    <DetailItem label={t("session.duration")} value={details.sessionDuration} />
                    <DetailItem label={t("session.energyCharged")} value={formatDecimal(details.energyChargedKwh, " kWh")} />
                    <DetailItem label={t("session.peakPower")} value={formatDecimal(details.peakPowerKw, " kW", 1)} />
                    <DetailItem
                        label={t("session.battery")}
                        value={formatPercentRange(details.socStartPercent, details.socEndPercent)}
                    />
                </dl>

                {showCdrFields && (
                    <div className="mt-4 border-t border-mute-200 pt-4">
                        <h4 className="mb-3 text-sm font-bold text-black">{t("session.cdrInformation")}</h4>

                        <dl className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-5">
                            <DetailItem label={t("session.cdrPublicId")} value={details.cdrPublicId} />
                            <DetailItem label={t("session.cdrSent")} value={details.cdrSent} />
                            {hasCreditCdr && (
                                <>
                                    <DetailItem label={t("session.creditCdrPublicId")} value={details.creditCdrPublicId} />
                                    <DetailItem label={t("session.creditCdrSent")} value={details.creditCdrSent} />
                                </>
                            )}
                            <DetailItem
                                label={t("session.invoiced")}
                                value={
                                    details.isCdrInvoiced == null
                                        ? null
                                        : details.isCdrInvoiced
                                            ? t("common.yes")
                                            : t("common.no")
                                }
                            />
                        </dl>

                        {canCreditCdr && (
                            <div className="mt-4">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    disabled={crediting}
                                    onClick={() => setCreditModalOpen(true)}
                                >
                                    {crediting ? t("session.creatingCreditCdr") : t("entity.creditCdr")}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
            <div className={cardClassName}>
                <h3 className="mb-3 text-sm font-bold text-black">{t("session.costs.title")}</h3>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-5">
                    <DetailItem
                        label={t("session.costPerKwhInclVat")}
                        value={
                            details.costPerKwh == null
                                ? null
                                : `${formatDecimal(details.costPerKwh)} ${details.currencyCode || "SEK"}`
                        }
                    />
                    {costRows.map((row) => (
                        <DetailItem key={row.label} label={row.label} value={row.value} />
                    ))}
                    <DetailItem
                        label={t("session.totalCostInclVat")}
                        value={
                            totalCostDisplay === "-" ? (
                                "-"
                            ) : (
                                <span className="inline-flex items-center gap-1.5">
                                    {totalCostDisplay}
                                    {details.emspSessionFinalized === false && (
                                        <FontAwesomeIcon
                                            icon={faCircleDollar}
                                            title={t("session.notFinalizedEmsp")}
                                            style={getStatusColorStyle("ERROR")}
                                        />
                                    )}
                                    {cardPaymentWarning && (
                                        <FontAwesomeIcon
                                            icon={faCircleDollar}
                                            title={t(cardPaymentWarning)}
                                            style={getStatusColorStyle("ERROR")}
                                        />
                                    )}
                                </span>
                            )
                        }
                    />
                    <DetailItem
                        label={t("form.paymentMethod")}
                        value={formatPaymentMethod(details.paymentMethodName, t)}
                    />
                    {!showReceipt && details.stripePaymentIntentId ? (
                        <DetailItem
                            label={t("session.stripePaymentIntent")}
                            value={details.stripePaymentIntentId}
                        />
                    ) : null}
                </dl>
            </div>

            {showReceipt && (
                <div className={`${cardClassName} lg:col-span-2`}>
                    <h3 className="mb-3 text-sm font-bold text-black">{t("session.receipt.title")}</h3>

                    <FormGrid className="gap-y-0">
                        <DetailRow label={t("session.receipt.number")} value={receipt.receiptNumber} />
                        <DetailRow label={t("session.receipt.seller")} value={receipt.sellerName} />
                        <DetailRow
                            label={t("session.receipt.issued")}
                            value={receipt.issuedAtUtc ? formatSwedishTime(receipt.issuedAtUtc) : null}
                        />
                        <DetailRow
                            label={t("session.receipt.paymentStatus")}
                            value={formatPaymentStatus(receipt.paymentStatus, t)}
                        />
                        <DetailRow label={t("session.receipt.paidAmount")} value={formatReceiptMoney(receipt.paidAmount)} />
                        {details.stripePaymentIntentId ? (
                            <DetailRow
                                label={t("session.stripePaymentIntent")}
                                value={details.stripePaymentIntentId}
                            />
                        ) : null}
                        <div className="md:col-span-2">
                            <InputRow
                                label={t("common.email")}
                                value={receiptEmail}
                                invalid={receiptEmailInvalid}
                                disabled={sendingReceipt}
                                onChange={(value) => {
                                    setReceiptEmail(value);
                                    if (receiptEmailInvalid) {
                                        setReceiptEmailInvalid(!isReceiptEmail(value));
                                    }
                                }}
                                action={
                                    <>
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary w-full sm:w-auto"
                                            disabled={sendingReceipt}
                                            onClick={handleSendReceipt}
                                        >
                                            {sendingReceipt ? t("session.receipt.sending") : t("session.receipt.send")}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary w-full sm:w-auto"
                                            disabled={openingReceipt}
                                            onClick={handleOpenReceipt}
                                        >
                                            {openingReceipt ? t("session.receipt.opening") : t("session.receipt.openPdf")}
                                        </button>
                                    </>
                                }
                            />
                        </div>
                    </FormGrid>
                </div>
            )}
            </div>

            <ConfirmationModal
                isOpen={creditModalOpen}
                title={creditConfirmTitle}
                description={creditConfirmDescription}
                okText={t("session.creditCdrConfirmOk")}
                onClose={() => setCreditModalOpen(false)}
                onConfirm={handleCreditCdrConfirm}
            />
        </div>
    );
}

export default SessionDetailsSummary;
